/**
 * Hand-picked links from the site's most-crawled pages into its least-linked ones.
 *
 * Why this exists: a SearchHandled crawl and GSC agreed on the diagnosis from
 * two directions. GSC's "Discovered - currently not indexed" bucket held 33
 * URLs that Google knows about and has never once fetched, and counting inbound
 * links across the source found 46 posts with zero editorial links pointing at
 * them — they were reachable only through the automatic related-post ring and
 * the paginated index, which is the weakest possible signal.
 *
 * The underlying constraint is authority. DataForSEO puts the domain at 20
 * referring domains and rank 50 against a direct competitor's 263 and 219, so
 * Google rations crawl over 523 URLs. When crawl is rationed, what decides
 * which pages get fetched is internal link weight, and ours is extremely
 * top-heavy: nav and footer pages carry ~470 internal links each while a buried
 * 2025 article carries a handful.
 *
 * A link from a page Google crawls constantly is worth far more than a sitemap
 * entry. So each key below is a page with real, measured traffic, and each
 * value is a genuinely related article that currently has no editorial links at
 * all. Every pairing is a real topical relationship — this is a distribution
 * fix, not a link farm, and a rail of irrelevant links would be worse than none.
 *
 * Traffic figures are GSC clicks over the 28 days to 2026-08-09.
 */
export interface EditorialLink {
  slug: string
  /** Why a reader of the source page would want this. Shown as the rail copy. */
  reason: string
}

export const EDITORIAL_LINKS: Record<string, EditorialLink[]> = {
  // 333 clicks — the single biggest page on the site.
  'does-fairlife-have-microplastics': [
    { slug: 'microplastics-in-coffee-creamer', reason: 'The other dairy product that lives in a plastic bottle in your fridge door' },
    { slug: 'do-energy-drinks-have-microplastics', reason: 'Same ready-to-drink format question, different aisle' },
    { slug: 'how-much-plastic-do-we-eat-per-week', reason: 'Where a single shake actually sits in your weekly total' },
  ],

  // 208 clicks.
  'nespresso-vertuo-vs-original-microplastics': [
    { slug: 'do-ninja-coffee-makers-have-microplastics', reason: 'If you are reconsidering the machine as well as the pod' },
    { slug: 'microplastics-in-coffee-creamer', reason: 'The part of your coffee nobody audits' },
  ],

  // 196 clicks.
  'microplastics-in-k-cups-and-coffee-pods': [
    { slug: 'do-ninja-coffee-makers-have-microplastics', reason: 'The machine underneath the pod' },
    { slug: 'microplastics-in-coffee-creamer', reason: 'What you add after the brew' },
  ],

  // 194 clicks.
  'chewing-gum-brands-ranked-microplastics': [
    { slug: 'does-pur-gum-have-microplastics', reason: 'The brand most often recommended as the clean alternative' },
    { slug: 'do-zyn-pouches-have-microplastics', reason: 'The other thing people keep in their mouth for an hour' },
    { slug: 'does-vaping-produce-microplastics', reason: 'Inhaled rather than chewed, and far less studied' },
    // Added 2026-08-12. This page's readers arrive on the "wait, is this thing
    // I put on my body actually plastic?" question, which is exactly the
    // contact lens piece.
    { slug: 'do-contact-lenses-shed-microplastics', reason: 'The plastic 45 million people wear on their eye for ten hours a day' },
  ],

  // 189 clicks, and the largest impression pool on the site.
  'do-ziploc-bags-release-microplastics': [
    { slug: 'microplastics-in-sous-vide', reason: 'The same bags, deliberately heated for hours' },
    { slug: 'microplastics-in-bread', reason: 'What the bag does to the thing you store in it' },
  ],

  // 125 clicks.
  'microplastics-in-espresso-machines': [
    { slug: 'do-ninja-coffee-makers-have-microplastics', reason: 'The drip-and-espresso hybrid most people actually own' },
  ],

  // 118 clicks. Now the toothpaste cluster's canonical page after the
  // 2026-08-09 consolidation, so it is the natural parent for both brand audits.
  'best-microplastic-free-toothpaste': [
    { slug: 'does-crest-have-microplastics', reason: 'All 50 Crest formulas, checked against the FDA filings' },
    { slug: 'does-sensodyne-have-microplastics', reason: 'All 14 Sensodyne and Pronamel formulas' },
    // Added 2026-08-12. Same reader frame — a daily personal-care product that
    // turns out to be a plastic formulation — and the nail post launched with
    // no inbound editorial links at all.
    { slug: 'do-gel-and-acrylic-nails-shed-microplastics', reason: 'The bathroom product with the longest legal exemption of any category' },
  ],

  // The food hub — the natural parent for the single-food investigations that
  // nothing else links to.
  'foods-with-the-most-microplastics': [
    { slug: 'fruit-vegetables-biggest-microplastic-source-diet', reason: 'The finding that overturns the usual seafood answer' },
    { slug: 'microplastics-in-eggs', reason: 'Unexpected, and the packaging is only half the story' },
    { slug: 'microplastics-in-honey', reason: 'Contaminated before it reaches a jar' },
    { slug: 'microplastics-in-chocolate', reason: 'Wrapper, mould and conching equipment' },
  ],

  // 110 clicks.
  'are-vacuum-sealer-bags-safe-microplastics': [
    { slug: 'microplastics-in-sous-vide', reason: 'The same bags at 60°C for six hours' },
  ],

  // 107 clicks.
  'best-glass-water-bottles': [
    { slug: 'microplastics-in-tap-water-by-us-city', reason: 'What you are putting in the bottle matters as much as the bottle' },
  ],

  // 100 clicks.
  'microplastics-in-canned-food': [
    { slug: 'microplastics-in-pasta', reason: 'The other pantry staple, and the water you boil it in' },
    { slug: 'microplastics-in-cereal', reason: 'Where the liner and the bag both contribute' },
  ],

  // ── App comparison, added 14 Aug 2026 ────────────────────────────────────
  // Placed on the cosmetics pages that already draw the audience most likely
  // to be running a hazard scanner in the aisle.
  'clean-beauty-microplastics-loophole': [
    { slug: 'think-dirty-vs-yuka', reason: 'Why a scanner app gives the same formula a clean score' },
  ],
  'worst-microplastic-ingredients': [
    { slug: 'think-dirty-vs-yuka', reason: 'These names pass Yuka and Think Dirty untouched, and why' },
  ],

  // ── Testing cluster, added 13 Aug 2026 ───────────────────────────────────
  // Inbound links for /blog/microplastic-testing, placed on the pages that
  // already earn clicks rather than on topically-adjacent pages that do not.
  // Click counts are the 28 days to 11 Aug 2026.

  // 105 clicks. Bottled-water figures vary by orders of magnitude and the
  // reason is method variation, which is exactly what the testing page covers.
  'microplastics-bottled-water-brands-ranked': [
    { slug: 'microplastic-testing', reason: 'Why two labs report wildly different counts for the same bottle' },
  ],

  // 87 clicks. Same problem at country scale — cross-study numbers are not
  // comparable without a shared method.
  'microplastics-in-drinking-water-by-country': [
    { slug: 'microplastic-testing', reason: 'What ISO 16094 changed, and why older figures do not compare' },
  ],

  // 76 clicks. The single most actionable use of a lab test is a before/after
  // pair across a filter, so this is the highest-intent placement on the site.
  'microplastics-water-filters-compared': [
    { slug: 'microplastic-testing', reason: 'How to verify your filter is actually working, rather than trusting the box' },
  ],

  // The DIY counterpart. These two must stay scoped apart: this one answers
  // "can I do it myself", the other "what can a laboratory measure".
  'how-to-test-for-microplastics-at-home': [
    { slug: 'microplastic-testing', reason: 'What a laboratory can measure once you have accepted you cannot do it at home' },
  ],
}

export function getEditorialLinks(slug: string): EditorialLink[] {
  return EDITORIAL_LINKS[slug] ?? []
}
